import { Allocation } from "./allocation.class";
import { AllocationAsObjectType } from "./allocation.type";
import { DEFAULT_CATEGORY_ID } from "@bitwarden/web-vault/app/models/constants/category.constants";
import { DEFAULT_CLASSIFICATION_ID } from "@bitwarden/web-vault/app/models/constants/classification.constants";

export class AllocationFactory {
  static createAllocation(
    categoryId: string,
    classificationId: string,
    split?: number,
  ): Allocation {
    const allocation = new Allocation();
    allocation.categoryLink.id = categoryId;
    allocation.classificationLink.id = classificationId;
    allocation.split = split ?? 0;
    return allocation;
  }

  static CreateAllocationFromObject(obj: AllocationAsObjectType): Allocation {
    const allocation = new Allocation();
    allocation.categoryLink.id = obj.ctId;
    allocation.classificationLink.id = obj.clId;
    allocation.split = Number(obj.spt);
    return allocation;
  }

  static createFromAllocationObjectArray(objArray: AllocationAsObjectType[]): Allocation[] {
    return objArray.map((obj) => this.CreateAllocationFromObject(obj));
  }

  static createFromAnyObject(obj: any): Allocation {
    return this.createAllocation(
      obj.categoryId || DEFAULT_CATEGORY_ID,
      obj.classificationId || DEFAULT_CLASSIFICATION_ID,
      obj.split || 0,
    );
  }

  static createFromAnyObjectArray(objArray: any[]): Allocation[] {
    return objArray.map((obj) => this.createFromAnyObject(obj));
  }

  static createAllocationFromExistingAllocation(allocation: Allocation, split: number): Allocation {
    return this.createAllocation(
      allocation.categoryLink.id,
      allocation.classificationLink.id,
      split,
    );
  }

  /**
   * Create new allocations from existing allocations but preserve the split ratios with the new amount
   *
   * @param allocations
   * @param amount
   */
  static createAllocationsFromAllocationsArray(
    allocations: Allocation[],
    amount: number,
  ): Allocation[] {
    const totalSplit = allocations.reduce((sum, allocation) => sum + allocation.split, 0);

    if (totalSplit === 0) {
      // If total split is 0, return allocations with 0 split to avoid division by zero
      return allocations.map((allocation) =>
        this.createAllocationFromExistingAllocation(allocation, 0),
      );
    }

    const newAllocations: Allocation[] = allocations.map((allocation) =>
      this.createAllocationFromExistingAllocation(
        allocation,
        Math.round((allocation.split / totalSplit) * amount * 100) / 100,
      ),
    );

    // check for rounding errors
    const newTotal = allocations.reduce((sum, allocation) => sum + allocation.split, 0);

    const roundingError = amount - newTotal;
    if (Math.abs(roundingError) > 0) {
      // Adjust the first allocation to account for rounding error
      newAllocations[0].split += roundingError;
    }

    return newAllocations;
  }
}
